import CloseIcon from '@mui/icons-material/Close'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import List from '@mui/material/List'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import React, { useEffect, useRef } from 'react'

import T from 'T'
import { formatPhone } from 'utils/helper'

import CallerMatchItem from './CallerMatchItem'
import { CallerMatch } from './screenPopApi'

import type { DialStackInstance } from '@dialstack/sdk-js'

// The call log lands after the call ends, so reload the history once the simulated call is over.
const HISTORY_RELOAD_MS = 45000

interface ScreenPopPanelProps {
  fromNumber: string
  fromName: string | null
  matches?: CallerMatch[]
  instance: DialStackInstance
  onClose: () => void
  onOpenCustomer: (accountId: string) => void
}

const ScreenPopPanel = ({ fromNumber, fromName, matches, instance, onClose, onOpenCustomer }: ScreenPopPanelProps) => {
  const historyRef = useRef<HTMLDivElement>(null)
  const match = matches?.length === 1 ? matches[0] : undefined
  const title = match?.accountName ?? fromName ?? formatPhone(fromNumber)

  useEffect(() => {
    const showHistory = () => {
      const history = instance.create('call-history')
      history.setPhoneNumber(fromNumber)
      historyRef.current?.replaceChildren(history)
    }
    showHistory()
    const timer = setTimeout(showHistory, HISTORY_RELOAD_MS)
    return () => clearTimeout(timer)
  }, [fromNumber, instance])

  return (
    <Paper
      elevation={8}
      sx={{ position: 'fixed', right: 16, bottom: 16, width: 380, maxWidth: 'calc(100vw - 32px)', zIndex: 'modal', overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <PhoneInTalkIcon sx={{ mt: 0.5 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption">{T.SCREEN_POP_INCOMING_CALL}</Typography>
          <Typography noWrap variant="h6">
            {title}
          </Typography>
          <Typography variant="body2">{formatPhone(fromNumber)}</Typography>
        </Box>
        <IconButton aria-label={T.CLOSE} size="small" sx={{ color: 'inherit' }} onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {!matches && <LinearProgress />}
      {matches?.length === 0 && (
        <Typography sx={{ p: 2 }} variant="body2">
          {T.SCREEN_POP_NO_MATCH}
        </Typography>
      )}
      {matches && matches.length > 0 && (
        <List dense disablePadding>
          {matches.map(m => (
            <CallerMatchItem key={m.accountId} isSelected={m === match} match={m} onOpen={onOpenCustomer} />
          ))}
        </List>
      )}

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }} variant="overline">
          {T.SCREEN_POP_RECENT_CALLS}
        </Typography>
        <div ref={historyRef} />
      </Box>
    </Paper>
  )
}

export default ScreenPopPanel
